import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import {
  DownloadOutlined,
  KeyboardArrowLeftOutlined,
  KeyboardArrowRightOutlined,
  KeyboardDoubleArrowLeftOutlined,
  KeyboardDoubleArrowRightOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import jsPDF from "jspdf";

import SearchSelect, { OptionItem } from "../../atoms/SearchSelect/SearchSelect";
import SelectField from "../../atoms/SelectField/SelectField";
import CpfField from "../../atoms/CpfField/CpfField";
import { findAllAcoesQualificacao, findAllTurmasAcao } from "../../../API/apiAcoes";
import { findRelatorioCertificadosEmitidos } from "../../../API/apiRelatoriosTurma";
import { reemitIssuedCertificate } from "../../../API/apiParticipantCertificates";
import { AcaoQualificacao, TurmaAcao } from "../../../types/acao";
import { CertificadoEmitidoItem } from "../../../types/relatoriosTurma";
import { exportXLSX } from "../../../utils/exporters/exportXLSX";
import {
  formatarCpf,
  formatarDatetimeCompletoBr,
  formatarDatetimeToDataBr,
} from "../../../utils/Masks";
import {
  buildCertificateVariableMap,
  getPdfPageSize,
  hydrateCertificatePage,
  renderCertificatePageToPdf,
  replaceCertificateVariables,
  replaceCertificateVariablesInHtml,
} from "../../../utils/certificateEmission";
import { registerCertificatePdfFonts } from "../../../utils/certificatePdfFonts";
import { CertificatePage } from "../../../types/CertificateTemplate";
import { ParticipantCertificateEmissionData } from "../../../types/ParticipantCertificate";
import { useLocalidadeStore } from "../../../store/useLocalidadeStore";
import { Cidade } from "../../../types/Cidade";
import { useAuth } from "../../../resources/AuthProvider";

import styles from "./ModelosCertificados.module.css";
import stylesFilter from "../../../styles/Filter.module.css";
import stylesInput from "../../../styles/Input.module.css";
import stylesTable from "../../../styles/Table.module.css";
import stylesButton from "../../../styles/Button.module.css";
import stylesGeneral from "../../../styles/General.module.css";
import stylesGrid from "../../../styles/Grid.module.css";
import stylesCertificate from "../Certificados/Certificados.module.css";

const perPageOptions = [{ nome: "10" }, { nome: "20" }, { nome: "30" }];

const columnsParticipanteBase = [
  { label: "Nome", key: "nome" },
  { label: "CPF", key: "cpf" },
  { label: "E-mail", key: "email" },
  { label: "Municipio", key: "municipio" },
  { label: "Atuacao", key: "atuacao" },
  { label: "Lotacao", key: "lotacao" },
  { label: "Vinculo", key: "vinculo" },
  { label: "Funcao", key: "funcao" },
  { label: "Superintendencia", key: "superintendencia" },
  { label: "Unidade", key: "unidade" },
  { label: "Diretoria Regional", key: "diretoria_regional" },
  { label: "Diretoria Interna", key: "diretoria_interna" },
  { label: "Orgao de Atuacao", key: "orgao_atuacao" },
  { label: "Instituicao", key: "instituicao" },
  { label: "Ambito de Atuacao", key: "ambito_atuacao" },
];

const getParticipantBaseData = (item: {
  participante: {
    nome: string;
    cpf: string;
    email: string;
    dados_profissionais?: any;
  };
}) => {
  const professional = item.participante?.dados_profissionais;

  return {
    nome: item.participante?.nome ?? "",
    cpf: formatarCpf(item.participante?.cpf ?? ""),
    email: item.participante?.email ?? "",
    municipio: professional?.municipio_nome ?? "",
    atuacao: professional?.orgao_nome ?? professional?.lotacao_nome ?? professional?.instituicao_nome ?? "",
    lotacao: professional?.lotacao_nome ?? "",
    vinculo: professional?.vinculo_nome ?? "",
    funcao: professional?.funcao_nome ?? "",
    superintendencia: professional?.superintendencia_nome ?? "",
    unidade: professional?.unidade_nome ?? "",
    diretoria_regional: professional?.diretoria_regional_nome ?? "",
    diretoria_interna: professional?.diretoria_interna_nome ?? "",
    orgao_atuacao: professional?.orgao_nome ?? "",
    instituicao: professional?.instituicao_nome ?? "",
    ambito_atuacao: professional?.ambito_nome ?? "",
  };
};

const buildPagination = (
  currentPage: number,
  totalPages: number,
  perPage: number,
  setCurrentPage: (page: number) => void,
  setPerPage: (value: number) => void,
) => (
  <div className={`${stylesTable.containerPagination} ${stylesGeneral.mt4}`}>
    <div className={stylesTable.qtdItems}>
      <div className={stylesTable.labelSelect}>Resultados por pagina</div>
      <SelectField
        label=""
        value={perPage.toString()}
        onChange={(value) => {
          setPerPage(Number(value));
          setCurrentPage(1);
        }}
        options={perPageOptions}
        disableClearable
        disableTyping
      />
    </div>
    <div className={stylesTable.pagination}>
      <IconButton className={currentPage === 1 ? stylesTable.iconButtonPageDisabled : stylesTable.iconButtonPage} disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>
        <KeyboardDoubleArrowLeftOutlined />
      </IconButton>
      <IconButton className={currentPage === 1 ? stylesTable.iconButtonPageDisabled : stylesTable.iconButtonPage} disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
        <KeyboardArrowLeftOutlined />
      </IconButton>
      <span className={stylesTable.pages}>Pagina {currentPage} de {totalPages}</span>
      <IconButton className={currentPage === totalPages ? stylesTable.iconButtonPageDisabled : stylesTable.iconButtonPage} disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
        <KeyboardArrowRightOutlined />
      </IconButton>
      <IconButton className={currentPage === totalPages ? stylesTable.iconButtonPageDisabled : stylesTable.iconButtonPage} disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>
        <KeyboardDoubleArrowRightOutlined />
      </IconButton>
    </div>
    <div className={stylesTable.setNumberPage}>
      <div className={stylesTable.labelSelect}>Ir para a pagina</div>
      <SelectField
        label=""
        value={currentPage.toString()}
        onChange={(value) => setCurrentPage(Number(value))}
        options={Array.from({ length: totalPages }, (_, index) => ({ nome: String(index + 1) }))}
        disableTyping
      />
    </div>
  </div>
);

type CertificatePreviewPages = {
  front: CertificatePage;
  back: CertificatePage | null;
};

const CertificatePreviewPage = ({
  page,
  emission,
  label,
}: {
  page: CertificatePage;
  emission: ParticipantCertificateEmissionData;
  label: string;
}) => {
  const variableMap = buildCertificateVariableMap(
    emission.dados,
    emission.codigo,
    formatarDatetimeToDataBr(emission.impresso_em),
  );
  const { width, height } = getPdfPageSize(emission.certificate_template.page.orientation);

  return (
    <div className={stylesCertificate.previewPage}>
      <div
        className={stylesCertificate.previewCanvas}
        style={{
          width,
          height,
          backgroundImage: page.background?.dataUrl ? `url(${page.background.dataUrl})` : "none",
          backgroundSize:
            page.background?.fit === "stretch"
              ? "100% 100%"
              : page.background?.fit || "contain",
        }}
      >
        {page.elements.map((element) => {
          const baseStyle = {
            left: element.position.x || 0,
            top: element.position.y || 0,
            width: element.size.width,
            height: element.size.height,
          };

          if (element.type === "html_text") {
            return (
              <div
                key={element.id}
                className={`${stylesCertificate.previewElement} ${stylesCertificate.previewText}`}
                style={{
                  ...baseStyle,
                  fontFamily: element.style.fontFamily,
                  fontSize: `${element.style.fontSize}px`,
                  color: element.style.color,
                  textAlign: element.style.align,
                }}
                dangerouslySetInnerHTML={{
                  __html: replaceCertificateVariablesInHtml(element.contentHtml, variableMap),
                }}
              />
            );
          }

          if (element.type === "image") {
            return (
              <div key={element.id} className={stylesCertificate.previewElement} style={baseStyle}>
                <img src={element.dataUrl} alt={element.label} className={stylesCertificate.previewImage} />
              </div>
            );
          }

          const qrImageSize = element.qrSize || Math.min(element.size.width || 120, 120);
          return (
            <div key={element.id} className={stylesCertificate.previewElement} style={baseStyle}>
              <div
                className={
                  element.textPosition === "bottom"
                    ? stylesCertificate.previewQrWrapBottom
                    : stylesCertificate.previewQrWrap
                }
              >
                {element.qrCodeDataUrl ? (
                  <img
                    src={element.qrCodeDataUrl}
                    alt="QR Code"
                    className={stylesCertificate.previewQrImage}
                    style={{ width: `${qrImageSize}px` }}
                  />
                ) : null}
                <div
                  className={stylesCertificate.previewQrText}
                  style={{
                    fontFamily: element.style.fontFamily,
                    fontSize: `${Math.max(element.style.fontSize * 0.16, 7)}px`,
                    color: element.style.color,
                  }}
                >
                  {element.institutionText ? <p>{replaceCertificateVariables(element.institutionText, variableMap)}</p> : null}
                  <p>
                    Chave de validação: <strong>{replaceCertificateVariables(element.validationKey, variableMap)}</strong>.
                    Data de emissão: <strong>{replaceCertificateVariables(element.issueDate, variableMap)}</strong>
                  </p>
                  <p>{replaceCertificateVariables(element.instructionText, variableMap)}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className={styles.previewPageLabel}>{label}</div>
    </div>
  );
};

const RelatorioCertificadosEmitidos = () => {
  const { hasActionPermission } = useAuth();
  const canViewIssuedCertificate = hasActionPermission("relatorios.view");
  const [loadingAcoes, setLoadingAcoes] = useState(false);
  const [loadingTurmas, setLoadingTurmas] = useState(false);
  const [loadingMunicipiosMg, setLoadingMunicipiosMg] = useState(false);
  const [loadingCertificados, setLoadingCertificados] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [msgError, setMsgError] = useState<string | null>(null);
  const [msgSuccess, setMsgSuccess] = useState<string | null>(null);
  const [acoes, setAcoes] = useState<AcaoQualificacao[]>([]);
  const [turmas, setTurmas] = useState<TurmaAcao[]>([]);
  const [municipiosMg, setMunicipiosMg] = useState<Cidade[]>([]);
  const [acaoId, setAcaoId] = useState<number | null>(null);
  const [turmaId, setTurmaId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [nome, setNome] = useState("");
  const [cpf, setCpf] = useState("");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [municipioId, setMunicipioId] = useState<number | null>(null);
  const [certificados, setCertificados] = useState<CertificadoEmitidoItem[]>([]);
  const [loadingCertificateCode, setLoadingCertificateCode] = useState<string | null>(null);
  const [previewEmission, setPreviewEmission] = useState<ParticipantCertificateEmissionData | null>(null);
  const [previewPages, setPreviewPages] = useState<CertificatePreviewPages | null>(null);

  const ensureEstados = useLocalidadeStore((state) => state.ensureEstados);
  const ensureCidades = useLocalidadeStore((state) => state.ensureCidades);

  useEffect(() => {
    const timer = msgError ? setTimeout(() => setMsgError(null), 5000) : undefined;
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [msgError]);

  useEffect(() => {
    const timer = msgSuccess ? setTimeout(() => setMsgSuccess(null), 5000) : undefined;
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [msgSuccess]);

  useEffect(() => {
    const loadAcoes = async () => {
      try {
        setLoadingAcoes(true);
        const response = await findAllAcoesQualificacao(1, 999999);
        setAcoes(response.acoes || []);
      } catch {
        setMsgError("Nao foi possivel carregar as acoes.");
      } finally {
        setLoadingAcoes(false);
      }
    };

    void loadAcoes();
  }, []);

  useEffect(() => {
    const loadMunicipiosMg = async () => {
      try {
        setLoadingMunicipiosMg(true);
        const estados = await ensureEstados();
        const estadoMg = estados.find((item) => item.sigla === "MG");

        if (!estadoMg) {
          setMunicipiosMg([]);
          return;
        }

        const cidades = await ensureCidades({ estado_id: estadoMg.estado_id });
        setMunicipiosMg(cidades || []);
      } catch {
        setMsgError("Nao foi possivel carregar os municipios de MG.");
      } finally {
        setLoadingMunicipiosMg(false);
      }
    };

    void loadMunicipiosMg();
  }, [ensureCidades, ensureEstados]);

  useEffect(() => {
    const loadTurmas = async () => {
      if (!acaoId) {
        setTurmas([]);
        setTurmaId(null);
        return;
      }

      try {
        setLoadingTurmas(true);
        const result = await findAllTurmasAcao(String(acaoId));
        setTurmas(result || []);
      } catch {
        setMsgError("Nao foi possivel carregar as turmas da acao.");
        setTurmas([]);
      } finally {
        setLoadingTurmas(false);
      }
    };

    setTurmaId(null);
    void loadTurmas();
  }, [acaoId]);

  useEffect(() => {
    const loadCertificados = async () => {
      try {
        setLoadingCertificados(true);
        const response = await findRelatorioCertificadosEmitidos({
          page: currentPage,
          perPage,
          acao_id: acaoId || undefined,
          turma_id: turmaId || undefined,
          data_inicio: dataInicio || undefined,
          data_fim: dataFim || undefined,
          nome: nome.trim() || undefined,
          cpf: cpf.replace(/\D/g, "") || undefined,
          municipio: municipioId || undefined,
        });
        setCertificados(response.data || []);
        setTotalPages(response.pagination?.last_page || 1);
      } catch {
        setMsgError("Nao foi possivel carregar o relatorio de certificados emitidos.");
      } finally {
        setLoadingCertificados(false);
      }
    };

    void loadCertificados();
  }, [acaoId, turmaId, currentPage, perPage, dataInicio, dataFim, nome, cpf, municipioId]);

  const acaoOptions = useMemo<OptionItem[]>(() => acoes.map((acao) => ({ id: acao.id, nome: acao.nome })), [acoes]);
  const turmaOptions = useMemo<OptionItem[]>(() => turmas.map((turma) => ({ id: turma.id, nome: turma.nome })), [turmas]);
  const municipioOptions = useMemo<OptionItem[]>(() => municipiosMg.map((municipio) => ({ id: municipio.id, nome: municipio.nome })), [municipiosMg]);

  const handleExport = async () => {
    try {
      setLoadingExport(true);
      const response = await findRelatorioCertificadosEmitidos({
        page: 1,
        perPage: 999999,
        acao_id: acaoId || undefined,
        turma_id: turmaId || undefined,
        data_inicio: dataInicio || undefined,
        data_fim: dataFim || undefined,
        nome: nome.trim() || undefined,
        cpf: cpf.replace(/\D/g, "") || undefined,
        municipio: municipioId || undefined,
      });

      const rows = (response.data || []).map((item) => ({
        ...getParticipantBaseData(item),
        acao: item.acao_nome ?? "",
        turma: item.turma_nome ?? "",
        modelo: item.certificate_template_name ?? "",
        codigo_certificado: item.codigo ?? "",
        data_hora_emissao: formatarDatetimeCompletoBr(item.impresso_em) ?? "",
      }));

      exportXLSX(
        [
          { label: "Acao", key: "acao" },
          { label: "Turma", key: "turma" },
          { label: "Modelo", key: "modelo" },
          ...columnsParticipanteBase,
          { label: "Codigo", key: "codigo_certificado" },
          { label: "Data e hora de emissao", key: "data_hora_emissao" },
        ],
        rows,
        "certificados_emitidos.xlsx",
      );
      setMsgSuccess("Exportacao do relatorio de certificados iniciada.");
    } catch {
      setMsgError("Nao foi possivel exportar o relatorio de certificados.");
    } finally {
      setLoadingExport(false);
    }
  };

  const prepareCertificate = async (codigo: string) => {
    const emission = await reemitIssuedCertificate(codigo);
    const variableMap = buildCertificateVariableMap(
      emission.dados,
      emission.codigo,
      formatarDatetimeToDataBr(emission.impresso_em),
    );
    const front = await hydrateCertificatePage(emission.certificate_template.pages.front, variableMap);
    const back = emission.certificate_template.pages.back
      ? await hydrateCertificatePage(emission.certificate_template.pages.back, variableMap)
      : null;

    return { emission, pages: { front, back } };
  };

  const handlePreviewCertificate = async (codigo: string) => {
    try {
      setLoadingCertificateCode(codigo);
      const prepared = await prepareCertificate(codigo);
      setPreviewEmission(prepared.emission);
      setPreviewPages(prepared.pages);
    } catch (error: any) {
      setMsgError(error?.message || "Não foi possível preparar a visualização do certificado.");
    } finally {
      setLoadingCertificateCode(null);
    }
  };

  const handleDownloadCertificate = async (codigo: string) => {
    try {
      setLoadingCertificateCode(codigo);
      const { emission, pages } = await prepareCertificate(codigo);
      const orientation = emission.certificate_template.page.orientation;
      const { width, height } = getPdfPageSize(orientation);
      const variableMap = buildCertificateVariableMap(
        emission.dados,
        emission.codigo,
        formatarDatetimeToDataBr(emission.impresso_em),
      );
      const document = new jsPDF({
        orientation,
        unit: "px",
        format: [width, height],
        compress: true,
      });
      await registerCertificatePdfFonts(document);

      renderCertificatePageToPdf(document, pages.front, orientation, variableMap);
      if (pages.back?.enabled) {
        document.addPage([width, height], orientation);
        renderCertificatePageToPdf(document, pages.back, orientation, variableMap);
      }
      document.save(`certificado-${emission.codigo}.pdf`);
    } catch (error: any) {
      setMsgError(error?.message || "Não foi possível baixar o certificado.");
    } finally {
      setLoadingCertificateCode(null);
    }
  };

  return (
    <div className={styles.card}>
      <div className={styles.title}>Certificados Emitidos</div>
      
      <section className={stylesGeneral.mt4}>
        <div className={stylesFilter.container}>
          <div className={stylesGrid.grid12}>
            <div className={stylesFilter.inputGroup4}>
              <label className={stylesInput.titleInput}>Acao</label>
              <SearchSelect
                label=""
                options={acaoOptions}
                value={acaoId}
                onChange={(id) => {
                  setAcaoId(id);
                  setCurrentPage(1);
                }}
                disabled={loadingAcoes}
                paperClassname={stylesInput.paper}
                popperClassname={stylesInput.popper}
                labelClassname={stylesInput.labelSelect}
                inputClassname={stylesInput.inputSelect}
              />
            </div>
            <div className={stylesFilter.inputGroup4}>
              <label className={stylesInput.titleInput}>Turma</label>
              <SearchSelect
                label=""
                options={turmaOptions}
                value={turmaId}
                onChange={(id) => {
                  setTurmaId(id);
                  setCurrentPage(1);
                }}
                disabled={!acaoId || loadingTurmas}
                paperClassname={stylesInput.paper}
                popperClassname={stylesInput.popper}
                labelClassname={stylesInput.labelSelect}
                inputClassname={stylesInput.inputSelect}
              />
            </div>
            <div className={stylesFilter.inputGroup4}>
              <label className={stylesInput.titleInput}>Nome do usuario</label>
              <TextField
                size="small"
                variant="standard"
                fullWidth
                value={nome}
                onChange={(event) => {
                  setNome(event.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Digite o nome do participante"
                InputProps={{ className: stylesInput.input, disableUnderline: true }}
              />
            </div>
            <div className={stylesFilter.inputGroup4}>
              <label className={stylesInput.titleInput}>CPF</label>
              <CpfField
                value={cpf}
                onUpdate={(value) => {
                  setCpf(value);
                  setCurrentPage(1);
                }}
                customClassName={stylesInput.input}
              />
            </div>
            
            <div className={stylesFilter.inputGroup4}>
              <label className={stylesInput.titleInput}>Data emissao inicio</label>
              <TextField
                type="date"
                size="small"
                variant="standard"
                fullWidth
                value={dataInicio}
                onChange={(event) => {
                  setDataInicio(event.target.value);
                  setCurrentPage(1);
                }}
                InputLabelProps={{ shrink: true }}
                InputProps={{ className: stylesInput.input, disableUnderline: true }}
              />
            </div>
            <div className={stylesFilter.inputGroup4}>
              <label className={stylesInput.titleInput}>Data emissao fim</label>
              <TextField
                type="date"
                size="small"
                variant="standard"
                fullWidth
                value={dataFim}
                onChange={(event) => {
                  setDataFim(event.target.value);
                  setCurrentPage(1);
                }}
                InputLabelProps={{ shrink: true }}
                InputProps={{ className: stylesInput.input, disableUnderline: true }}
              />
            </div>
            <div className={stylesFilter.inputGroup6}>
              <label className={stylesInput.titleInput}>Municipio</label>
              <SearchSelect
                label=""
                options={municipioOptions}
                value={municipioId}
                onChange={(id) => {
                  setMunicipioId(id);
                  setCurrentPage(1);
                }}
                disabled={loadingMunicipiosMg}
                paperClassname={stylesInput.paper}
                popperClassname={stylesInput.popper}
                labelClassname={stylesInput.labelSelect}
                inputClassname={stylesInput.inputSelect}
              />
            </div>
            <div className={stylesFilter.inputGroup6} style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>
              <Button
                variant="outlined"
                className={stylesButton.buttonSquareSecondary}
                onClick={() => {
                  setAcaoId(null);
                  setTurmaId(null);
                  setNome("");
                  setCpf("");
                  setMunicipioId(null);
                  setDataInicio("");
                  setDataFim("");
                  setCurrentPage(1);
                }}
              >
                Limpar filtros
              </Button>
              <Button
                variant="contained"
                className={`${stylesButton.buttonSquarePrimary} ${loadingExport ? styles.disabledAction : ""}`}
                onClick={handleExport}
                startIcon={<DownloadOutlined />}
                disabled={loadingExport}
              >
                {loadingExport ? "Exportando..." : "Exportar XLS"}
              </Button>
            </div>
          </div>
        </div>


        <TableContainer className={stylesTable.tableContainer}>
          {loadingCertificados ? (
            <div className={styles.loader}>
              <CircularProgress />
            </div>
          ) : (
            <Table size="small">
              <TableHead className={stylesTable.tableHead}>
                <TableRow>
                  <TableCell padding="none" className={stylesTable.tableHeadRow}>Acao</TableCell>
                  <TableCell padding="none" className={stylesTable.tableHeadRow}>Turma</TableCell>
                  <TableCell padding="none" className={stylesTable.tableHeadRow}>Modelo</TableCell>
                  <TableCell padding="none" className={stylesTable.tableHeadRow}>Nome</TableCell>
                  <TableCell padding="none" className={stylesTable.tableHeadRow}>CPF</TableCell>
                  <TableCell padding="none" className={stylesTable.tableHeadRow}>Ambito de Atuacao</TableCell>
                  <TableCell padding="none" className={stylesTable.tableHeadRow}>Municipio</TableCell>
                  <TableCell padding="none" className={stylesTable.tableHeadRow}>Lotacao / Orgao</TableCell>
                  <TableCell padding="none" className={stylesTable.tableHeadRow}>Data e hora de emissao</TableCell>
                  <TableCell padding="none" className={stylesTable.tableHeadRow}>Codigo</TableCell>
                  {canViewIssuedCertificate && (
                    <TableCell padding="none" className={stylesTable.tableHeadRow}>Ações</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {certificados.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canViewIssuedCertificate ? 11 : 10} align="center" className={stylesTable.textXs}>
                      Nenhum registro encontrado.
                    </TableCell>
                  </TableRow>
                ) : (
                  certificados.map((item, index) => (
                    <TableRow key={item.id} className={`${stylesTable.tableRow} ${index % 2 === 0 ? stylesTable.tableRowEven : stylesTable.tableRowOdd}`}>
                      <TableCell padding="none" className={stylesTable.textXs}>{item.acao_nome || "-"}</TableCell>
                      <TableCell padding="none" className={stylesTable.textXs}>{item.turma_nome || "-"}</TableCell>
                      <TableCell padding="none" className={stylesTable.textXs}>{item.certificate_template_name || "-"}</TableCell>
                      <TableCell padding="none" className={stylesTable.textXs}>{item.participante?.nome}</TableCell>
                      <TableCell padding="none" className={stylesTable.textXs}>{formatarCpf(item.participante?.cpf)}</TableCell>
                      <TableCell padding="none" className={stylesTable.textXs}>{item.participante?.dados_profissionais?.ambito_nome || "-"}</TableCell>
                      <TableCell padding="none" className={stylesTable.textXs}>{item.participante?.dados_profissionais?.municipio_nome || "-"}</TableCell>
                      <TableCell padding="none" className={stylesTable.textXs}>
                        {item.participante?.dados_profissionais?.lotacao_nome || item.participante?.dados_profissionais?.orgao_nome || "-"}
                      </TableCell>
                      <TableCell padding="none" className={stylesTable.textXs}>{formatarDatetimeCompletoBr(item.impresso_em) || "-"}</TableCell>
                      <TableCell padding="none" className={stylesTable.textXs}>{item.codigo || "-"}</TableCell>
                      {canViewIssuedCertificate && (
                        <TableCell padding="none" className={stylesTable.textXs}>
                          <div className={styles.certificateActions}>
                            <IconButton
                              size="small"
                              aria-label="Visualizar certificado"
                              title="Visualizar certificado"
                              className={styles.certificateActionButton}
                              disabled={loadingCertificateCode === item.codigo}
                              onClick={() => handlePreviewCertificate(item.codigo)}
                            >
                              {loadingCertificateCode === item.codigo ? <CircularProgress size={16} /> : <VisibilityOutlined fontSize="small" />}
                            </IconButton>
                            <IconButton
                              size="small"
                              aria-label="Baixar certificado"
                              title="Baixar certificado"
                              className={styles.certificateActionButton}
                              disabled={loadingCertificateCode === item.codigo}
                              onClick={() => handleDownloadCertificate(item.codigo)}
                            >
                              <DownloadOutlined fontSize="small" />
                            </IconButton>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {buildPagination(currentPage, totalPages, perPage, setCurrentPage, setPerPage)}
      </section>

      <Dialog
        open={Boolean(previewEmission && previewPages)}
        onClose={() => {
          setPreviewEmission(null);
          setPreviewPages(null);
        }}
        fullWidth
        maxWidth="xl"
      >
        <DialogTitle>Pré-visualização do certificado</DialogTitle>
        <DialogContent dividers>
          {previewEmission && previewPages ? (
            <div className={stylesCertificate.previewStack}>
              <CertificatePreviewPage page={previewPages.front} emission={previewEmission} label="Frente" />
              {previewPages.back?.enabled ? (
                <CertificatePreviewPage page={previewPages.back} emission={previewEmission} label="Verso" />
              ) : null}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RelatorioCertificadosEmitidos;
